// src/firestoreService.js - WITH LOCAL AUDIO FILENAME & EXPIRATION

import { 
  collection, 
  addDoc, 
  updateDoc,
  deleteDoc,
  doc, 
  query, 
  where, 
  orderBy, 
  getDocs,
  getDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './firebase';
import { nanoid } from 'nanoid';

/**
 * Generate a short title from prompt text (5-6 words)
 */
const generateTitle = (promptText) => {
  if (!promptText) return 'Untitled Presentation';
  
  const words = promptText.trim().split(/\s+/);
  const shortTitle = words.slice(0, 6).join(' ');
  
  // If it ends mid-sentence, remove trailing punctuation
  return shortTitle.replace(/[,;:]$/, '');
};

/**
 * ✅ UPDATED: Save a new presentation with local audio filename support
 * @param {Object} presentationData - The presentation data to save
 * @returns {Promise<string>} - The document ID of the saved presentation
 */
export const savePresentation = async (presentationData) => {
  try {
    const {
      userId,
      uploadMode,
      userPrompt,
      requestedSlideCount,
      processedSlides,
      slideTemplates,
      slideImages,
      creatorName,
      creatorEmail,
      audioFileName, // ✅ CHANGED: From audioUrl to audioFileName
      audioExpiresAt, // ✅ NEW: When audio expires
      userUploadedFileName
    } = presentationData;

    // Validate required fields
    if (!userId) throw new Error('User ID is required');
    if (!processedSlides || processedSlides.length === 0) {
      throw new Error('No slides to save');
    }

    // Generate title from prompt
    const title = generateTitle(userPrompt || processedSlides[0]?.extractedContent?.heading);

    // ✅ UPDATED: Prepare slides data with timestamp info
    const slides = processedSlides.map(slide => ({
      extractedContent: slide.extractedContent,
      duration: slide.duration || 5, // ✅ NEW: Save duration
      startTime: slide.startTime || null, // ✅ NEW: Save startTime
      endTime: slide.endTime || null, // ✅ NEW: Save endTime
      audioSegments: slide.audioSegments || null // ✅ NEW: Save segment IDs
    }));

    // Prepare image URLs (filter out null/undefined)
    const imageUrls = slideImages
      .map(img => img?.image?.url || null);

    // ✅ UPDATED: Create presentation document with local audio support
    const presentationDoc = {
      userId,
      title,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      slideCount: slides.length,
      uploadMode: uploadMode || 'prompt',
      
      // Original input
      originalPrompt: userPrompt || '',
      requestedSlideCount: requestedSlideCount || slides.length,
      
      // ✅ NEW: Audio data (local storage)
      audioFileName: audioFileName || null, // ✅ CHANGED: Local filename
      audioExpiresAt: audioExpiresAt || null, // ✅ NEW: Expiration timestamp
      hasAudioSync: !!audioFileName, // Flag for UI (can we do synced MP4?)
      userUploadedFileName: userUploadedFileName || null,
      
      // Core data
      slides,
      templateIds: slideTemplates || [],
      imageUrls: imageUrls || [],
      
      // Share settings (initially not shared)
      isPublic: false,
      shareId: null,
      sharedAt: null,
      
      // Creator info for sharing
      creatorName: creatorName || 'Anonymous',
      creatorEmail: creatorEmail || ''
    };

    // Save to Firestore
    const docRef = await addDoc(collection(db, 'presentations'), presentationDoc);
    
    
    
    return docRef.id;
    
  } catch (error) {
    
    throw error;
  }
};

/**
 * Update an existing presentation
 * @param {string} presentationId - The document ID
 * @param {Object} updates - Fields to update
 */
export const updatePresentation = async (presentationId, updates) => {
  try {
    const docRef = doc(db, 'presentations', presentationId);
    
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
   
  } catch (error) {
    
    throw error;
  }
};

/**
 * Share a presentation (generates unique shareId)
 * @param {string} presentationId - The document ID
 * @param {string} userId - The user's ID (for verification)
 * @param {string} userName - The user's display name
 * @param {string} userEmail - The user's email
 * @returns {Promise<string>} - The generated shareId
 */
export const sharePresentation = async (presentationId, userId, userName, userEmail) => {
  try {
    // First, verify the presentation belongs to this user
    const docRef = doc(db, 'presentations', presentationId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Presentation not found');
    }
    
    if (docSnap.data().userId !== userId) {
      throw new Error('Unauthorized: You do not own this presentation');
    }
    
    // Check if already shared
    const existingShareId = docSnap.data().shareId;
    if (existingShareId) {
     
      return existingShareId;
    }
    
    // Generate unique shareId
    const shareId = nanoid(10);
    
    // Update presentation with share info
    await updateDoc(docRef, {
      isPublic: true,
      shareId: shareId,
      sharedAt: serverTimestamp(),
      creatorName: userName || 'Anonymous',
      creatorEmail: userEmail || '',
      updatedAt: serverTimestamp()
    });
    
   
    return shareId;
    
  } catch (error) {
    
    throw error;
  }
};

/**
 * Get presentation by shareId (for public viewing)
 * @param {string} shareId - The unique share ID
 * @returns {Promise<Object>} - The presentation data
 */
export const getPresentationByShareId = async (shareId) => {
  try {
    if (!shareId) throw new Error('Share ID is required');
    
    const q = query(
      collection(db, 'presentations'),
      where('shareId', '==', shareId),
      where('isPublic', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('Presentation not found or not shared');
    }
    
    const doc = querySnapshot.docs[0];
    
    
    return {
      id: doc.id,
      ...doc.data()
    };
    
  } catch (error) {
    
    throw error;
  }
};

/**
 * Unshare a presentation (revoke public access)
 * @param {string} presentationId - The document ID
 * @param {string} userId - The user's ID (for verification)
 */
export const unsharePresentation = async (presentationId, userId) => {
  try {
    const docRef = doc(db, 'presentations', presentationId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Presentation not found');
    }
    
    if (docSnap.data().userId !== userId) {
      throw new Error('Unauthorized: You do not own this presentation');
    }
    
    await updateDoc(docRef, {
      isPublic: false,
      updatedAt: serverTimestamp()
    });
    
    
    
  } catch (error) {
   
    throw error;
  }
};

/**
 * Get all presentations for a user
 * @param {string} userId - The user's ID
 * @returns {Promise<Array>} - Array of presentations
 */
export const getUserPresentations = async (userId) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const q = query(
      collection(db, 'presentations'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    
    const presentations = [];
    querySnapshot.forEach((doc) => {
      presentations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    
    return presentations;
    
  } catch (error) {
    
    throw error;
  }
};

/**
 * Load a single presentation by ID
 * @param {string} presentationId - The document ID
 * @returns {Promise<Object>} - The presentation data
 */
export const loadPresentation = async (presentationId) => {
  try {
    const docRef = doc(db, 'presentations', presentationId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      
      
      const data = docSnap.data();
      
      // ✅ UPDATED: Log audio info for debugging
      if (data.audioFileName) {
        
      }
      
      return {
        id: docSnap.id,
        ...data
      };
    } else {
      throw new Error('Presentation not found');
    }
    
  } catch (error) {
    
    throw error;
  }
};

/**
 * Delete a presentation
 * @param {string} presentationId - The document ID
 */
export const deletePresentation = async (presentationId) => {
  try {
    await deleteDoc(doc(db, 'presentations', presentationId));
  
  } catch (error) {
   
    throw error;
  }
};

/**
 * Format Firestore timestamp to readable date
 * @param {Timestamp} timestamp - Firestore timestamp
 * @returns {string} - Formatted date string
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    const date = timestamp.toDate();
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  } catch (error) {
    return '';
  }
};